/**
 * ConfigTablesSettings — Gestione tabelle di configurazione user-managed.
 * Implementato 30/06/2026 nell'ambito della feature "Tabelle di configurazione".
 *
 * Permessi:
 *   - Lettura: tutti gli operatori autenticati (per popolare i dropdown).
 *   - Mutation: solo amministratore / responsabile (gate backend via
 *     `verify_admin`). Il gate UI è applicato a monte da
 *     `ImpostazioniCombinate` (subtab visibile solo agli admin/resp).
 *
 * Performance Engineering Policy (30/06/2026):
 *   - Voci memoizzate via React.memo.
 *   - Refetch via `refreshConfigTables` solo dopo mutation, non in polling.
 *   - Ordinamento alfabetico già applicato dal backend.
 */
import React, { useState, useMemo, useCallback, memo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Plus, Pencil, Power, Trash2, Check, X, Tag, Building2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/api";

const SCOPE_LABELS = { studio: "Studio", agenzia: "Agenzia", both: "Entrambe" };
const SCOPE_BADGE_COLORS = {
  studio: "bg-blue-100 text-blue-800 border-blue-200",
  agenzia: "bg-orange-100 text-orange-800 border-orange-200",
  both: "bg-purple-100 text-purple-800 border-purple-200",
};

// ─── Singola riga voce tabella (memoizzata per evitare re-render
// inutili dell'intera lista quando si edita un singolo item). ──────
const ConfigEntryRow = memo(function ConfigEntryRow({
  entry,
  showScope,
  isEditing,
  editingName,
  editingScope,
  onStartEdit,
  onCancelEdit,
  onChangeEditingName,
  onChangeEditingScope,
  onSaveEdit,
  onToggle,
  onDelete,
  isBusy,
}) {
  if (isEditing) {
    return (
      <div className="flex items-center gap-2 py-2 px-3 bg-slate-50 border border-blue-300 rounded-md">
        <Input
          value={editingName}
          onChange={(e) => onChangeEditingName(e.target.value)}
          className="h-8 text-sm flex-1"
          data-testid={`ct-edit-name-${entry.id}`}
          autoFocus
        />
        {showScope && (
          <Select value={editingScope || entry.scope || "both"} onValueChange={onChangeEditingScope}>
            <SelectTrigger className="h-8 w-[120px] text-xs" data-testid={`ct-edit-scope-${entry.id}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="studio">Studio</SelectItem>
              <SelectItem value="agenzia">Agenzia</SelectItem>
              <SelectItem value="both">Entrambe</SelectItem>
            </SelectContent>
          </Select>
        )}
        <Button size="sm" onClick={onSaveEdit} disabled={isBusy} className="h-8 px-3 bg-green-600 hover:bg-green-700" data-testid={`ct-save-${entry.id}`}>
          <Check className="h-3.5 w-3.5" />
        </Button>
        <Button size="sm" variant="outline" onClick={onCancelEdit} disabled={isBusy} className="h-8 px-3" data-testid={`ct-cancel-edit-${entry.id}`}>
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>
    );
  }
  return (
    <div className={`flex items-center gap-2 py-2 px-3 rounded-md ${entry.isActive ? "bg-white" : "bg-slate-50 opacity-70"} border border-slate-200`}>
      <span className={`flex-1 text-sm ${entry.isActive ? "text-slate-800" : "text-slate-500 line-through"}`}>
        {entry.name}
      </span>
      {showScope && entry.scope && (
        <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${SCOPE_BADGE_COLORS[entry.scope] || ""}`}>
          {SCOPE_LABELS[entry.scope] || entry.scope}
        </Badge>
      )}
      <Badge variant={entry.isActive ? "default" : "secondary"} className="text-[10px] px-1.5 py-0">
        {entry.isActive ? "Attiva" : "Disattivata"}
      </Badge>
      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={onStartEdit} disabled={isBusy} title="Modifica nome" data-testid={`ct-edit-${entry.id}`}>
        <Pencil className="h-3.5 w-3.5" />
      </Button>
      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={onToggle} disabled={isBusy} title={entry.isActive ? "Disattiva" : "Attiva"} data-testid={`ct-toggle-${entry.id}`}>
        <Power className={`h-3.5 w-3.5 ${entry.isActive ? "text-green-600" : "text-slate-400"}`} />
      </Button>
      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 hover:bg-red-50" onClick={onDelete} disabled={isBusy} title="Elimina (solo se non usata)" data-testid={`ct-delete-${entry.id}`}>
        <Trash2 className="h-3.5 w-3.5 text-red-500" />
      </Button>
    </div>
  );
});

// ─── Sezione singola tabella (Tipo Pratica oppure Ente Coinvolto) ──
function ConfigTableSection({ title, icon: Icon, entries, tableKey, showScope, onChanged }) {
  const [newName, setNewName] = useState("");
  const [newScope, setNewScope] = useState("studio");
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [editingScope, setEditingScope] = useState("");
  const [busy, setBusy] = useState(false);

  // Filter ricerca / inattive
  const [search, setSearch] = useState("");
  const [showInactive, setShowInactive] = useState(true);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return entries
      .filter(e => showInactive || e.isActive)
      .filter(e => !q || e.name.toLowerCase().includes(q));
  }, [entries, search, showInactive]);

  const startEdit = useCallback((entry) => {
    setEditingId(entry.id);
    setEditingName(entry.name);
    setEditingScope(entry.scope || "");
  }, []);
  const cancelEdit = useCallback(() => {
    setEditingId(null);
    setEditingName("");
    setEditingScope("");
  }, []);

  const handleAdd = useCallback(async () => {
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    try {
      await api.createConfigEntry({
        table: tableKey,
        name,
        ...(showScope ? { scope: newScope } : {}),
      });
      toast.success(`Voce "${name}" aggiunta`);
      setNewName("");
      if (showScope) setNewScope("studio");
      await onChanged();
    } catch (e) {
      toast.error(e?.message || "Errore creazione voce");
    } finally {
      setBusy(false);
    }
  }, [newName, newScope, tableKey, showScope, onChanged]);

  const saveEdit = useCallback(async (entry) => {
    const name = editingName.trim();
    if (!name) {
      toast.error("Il nome non può essere vuoto");
      return;
    }
    setBusy(true);
    try {
      const patch = { name };
      if (showScope && editingScope && editingScope !== entry.scope) patch.scope = editingScope;
      await api.updateConfigEntry(entry.id, patch);
      toast.success(`Voce aggiornata`);
      cancelEdit();
      await onChanged();
    } catch (e) {
      toast.error(e?.message || "Errore aggiornamento");
    } finally {
      setBusy(false);
    }
  }, [editingName, editingScope, showScope, cancelEdit, onChanged]);

  const handleToggle = useCallback(async (entry) => {
    setBusy(true);
    try {
      await api.toggleConfigEntry(entry.id);
      toast.success(`Voce "${entry.name}" ${entry.isActive ? "disattivata" : "riattivata"}`);
      await onChanged();
    } catch (e) {
      toast.error(e?.message || "Errore toggle");
    } finally {
      setBusy(false);
    }
  }, [onChanged]);

  const handleDelete = useCallback(async (entry) => {
    if (!window.confirm(`Eliminare definitivamente la voce "${entry.name}"?\n\nL'operazione è consentita solo se la voce non è mai stata usata in una pratica.`)) return;
    setBusy(true);
    try {
      await api.deleteConfigEntry(entry.id);
      toast.success(`Voce "${entry.name}" eliminata`);
      await onChanged();
    } catch (e) {
      // Backend restituisce 409 con messaggio dettagliato (count usage).
      toast.error(e?.message || "Errore eliminazione");
    } finally {
      setBusy(false);
    }
  }, [onChanged]);

  return (
    <Card className="shadow-sm" data-testid={`ct-section-${tableKey}`}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Icon className="h-4 w-4 text-blue-600" />
          {title}
          <Badge variant="secondary" className="text-[10px] ml-1">
            {entries.filter(e => e.isActive).length} attive · {entries.length} totali
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Add new entry */}
        <div className="flex items-end gap-2 p-3 bg-blue-50/50 border border-blue-200 rounded-md">
          <div className="flex-1">
            <Label className="text-xs">Nuova voce</Label>
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !busy) { e.preventDefault(); handleAdd(); } }}
              placeholder="Inserisci nome..."
              className="h-8 text-sm"
              data-testid={`ct-new-name-${tableKey}`}
            />
          </div>
          {showScope && (
            <div className="w-[140px]">
              <Label className="text-xs">Scope</Label>
              <Select value={newScope} onValueChange={setNewScope}>
                <SelectTrigger className="h-8 text-sm" data-testid={`ct-new-scope-${tableKey}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="studio">Studio</SelectItem>
                  <SelectItem value="agenzia">Agenzia</SelectItem>
                  <SelectItem value="both">Entrambe</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <Button onClick={handleAdd} disabled={busy || !newName.trim()} className="h-8 px-3 bg-blue-600 hover:bg-blue-700" data-testid={`ct-add-btn-${tableKey}`}>
            <Plus className="h-4 w-4 mr-1" />
            Aggiungi
          </Button>
        </div>

        {/* Filters: search + show inactive */}
        <div className="flex items-center gap-2">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca..."
            className="h-7 text-xs flex-1"
            data-testid={`ct-search-${tableKey}`}
          />
          <label className="flex items-center gap-1 text-xs text-slate-600 cursor-pointer">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              data-testid={`ct-show-inactive-${tableKey}`}
            />
            Mostra disattivate
          </label>
        </div>

        {/* List */}
        <div className="space-y-1 max-h-[400px] overflow-y-auto">
          {filtered.length === 0 ? (
            <p className="text-xs text-slate-400 text-center py-4">Nessuna voce</p>
          ) : (
            filtered.map(entry => (
              <ConfigEntryRow
                key={entry.id}
                entry={entry}
                showScope={showScope}
                isEditing={editingId === entry.id}
                editingName={editingId === entry.id ? editingName : ""}
                editingScope={editingId === entry.id ? editingScope : ""}
                onStartEdit={() => startEdit(entry)}
                onCancelEdit={cancelEdit}
                onChangeEditingName={setEditingName}
                onChangeEditingScope={setEditingScope}
                onSaveEdit={() => saveEdit(entry)}
                onToggle={() => handleToggle(entry)}
                onDelete={() => handleDelete(entry)}
                isBusy={busy}
              />
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Componente principale ─────────────────────────────────────────
export default function ConfigTablesSettings({ configTables, refreshConfigTables }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="config-tables-settings">
      <ConfigTableSection
        title="Tipo Pratica"
        icon={Tag}
        entries={configTables.practice_types || []}
        tableKey="practice_type"
        showScope={false}
        onChanged={refreshConfigTables}
      />
      <ConfigTableSection
        title="Ente Coinvolto"
        icon={Building2}
        entries={configTables.ente_options || []}
        tableKey="ente_option"
        showScope={true}
        onChanged={refreshConfigTables}
      />
    </div>
  );
}
